import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate, Link } from 'react-router-dom';

const PrivacyPage = () => {
  const navigate = useNavigate();
  const lastUpdated = 'February 25, 2026';

  return (
    <div className="min-h-screen bg-obsidian text-text-primary">
      {/* Navigation */}
      <nav className="glass-card border-b border-white/5 sticky top-0 z-50">
        <div className="max-w-[1440px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 flex justify-between items-center">
          <Link to="/" className="font-playfair text-2xl sm:text-3xl font-semibold text-gold">ASTIIV BASGAT</Link>
          <Button 
            variant="ghost" 
            className="text-white/60 hover:text-white"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-gold/20 flex items-center justify-center mx-auto mb-6">
              <Shield className="w-8 h-8 text-gold" />
            </div>
            <h1 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-semibold mb-4">Privacy Policy</h1>
            <p className="text-text-secondary">Last updated: {lastUpdated}</p>
          </div>

          {/* Content */}
          <div className="prose prose-invert prose-gold max-w-none space-y-8">
            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">1. Introduction</h2>
              <p className="text-text-secondary leading-relaxed">
                ASTIIV BASGAT LLC ("we," "our," or "us") is committed to protecting your privacy and personal data. 
                This Privacy Policy explains how we collect, use, disclose, and safeguard your information when 
                you use our platform and services.
              </p>
              <p className="text-text-secondary leading-relaxed mt-4">
                We comply with the UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection (PDPL) and 
                other applicable data protection regulations in the United Arab Emirates, including DIFC Data 
                Protection Law and ADGM Data Protection Regulations where applicable.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">2. Information We Collect</h2>
              <p className="text-text-secondary leading-relaxed">
                We collect information that you provide directly to us, including:
              </p>
              
              <h3 className="text-lg font-semibold mt-6 mb-3 text-white">Personal Information</h3>
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                <li>Name, email address, and phone number</li>
                <li>Emirates ID or passport details (for property transactions)</li>
                <li>Residential address and nationality</li>
                <li>Financial information (income range, net worth for property matching)</li>
                <li>Lifestyle preferences and interests</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-white">Usage Information</h3>
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                <li>Device information and IP address</li>
                <li>Browser type and operating system</li>
                <li>Pages visited and features used</li>
                <li>Search queries and property preferences</li>
                <li>Interaction with listings and marketplace items</li>
              </ul>

              <h3 className="text-lg font-semibold mt-6 mb-3 text-white">Transaction Information</h3>
              <ul className="list-disc list-inside text-text-secondary space-y-2">
                <li>Payment and billing information</li>
                <li>Subscription history</li>
                <li>Property viewing requests and inquiries</li>
                <li>Communication records with partners and concierge</li>
              </ul>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">3. How We Use Your Information</h2>
              <p className="text-text-secondary leading-relaxed">
                We use the collected information for the following purposes:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Providing and personalizing our Services</li>
                <li>Processing transactions and subscriptions</li>
                <li>Matching you with suitable properties and lifestyle options</li>
                <li>Communicating with you about services, updates, and promotions</li>
                <li>Connecting you with verified partners (developers, agents, dealers)</li>
                <li>Improving our platform and developing new features</li>
                <li>Complying with legal obligations and UAE regulations</li>
                <li>Preventing fraud and ensuring platform security</li>
              </ul>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">4. Information Sharing</h2>
              <p className="text-text-secondary leading-relaxed">
                We may share your information with:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li><strong>Partner Businesses:</strong> Real estate developers, agents, and luxury dealers when you express interest in their listings</li>
                <li><strong>Service Providers:</strong> Payment processors, cloud hosting, analytics, and communication services</li>
                <li><strong>Legal Authorities:</strong> When required by UAE law, court order, or government request</li>
                <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                We do not sell your personal information to third parties for marketing purposes.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">5. Data Security</h2>
              <p className="text-text-secondary leading-relaxed">
                We implement appropriate technical and organizational measures to protect your personal data, including:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>SSL/TLS encryption for data transmission</li>
                <li>Secure cloud infrastructure with access controls</li>
                <li>Regular security assessments and updates</li>
                <li>Employee training on data protection</li>
                <li>Incident response procedures</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                However, no method of transmission over the Internet is 100% secure. We cannot guarantee 
                absolute security of your data.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">6. Data Retention</h2>
              <p className="text-text-secondary leading-relaxed">
                We retain your personal data for as long as necessary to:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Provide our Services to you</li>
                <li>Comply with legal obligations (typically 5-10 years for financial records under UAE law)</li>
                <li>Resolve disputes and enforce our agreements</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                When data is no longer needed, we securely delete or anonymize it in accordance with 
                UAE data protection requirements.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">7. Your Rights</h2>
              <p className="text-text-secondary leading-relaxed">
                Under UAE PDPL and applicable regulations, you have the right to:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Request correction of inaccurate data</li>
                <li><strong>Deletion:</strong> Request deletion of your data (subject to legal obligations)</li>
                <li><strong>Restriction:</strong> Request restriction of processing in certain circumstances</li>
                <li><strong>Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Objection:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Withdraw Consent:</strong> Withdraw consent at any time for consent-based processing</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                To exercise these rights, please contact us at privacy@dreamlife.ae
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">8. Cookies and Tracking</h2>
              <p className="text-text-secondary leading-relaxed">
                We use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Remember your preferences and settings</li>
                <li>Understand how you use our platform</li>
                <li>Improve our Services and user experience</li>
                <li>Provide personalized recommendations</li>
              </ul>
              <p className="text-text-secondary leading-relaxed mt-4">
                You can control cookies through your browser settings. Disabling cookies may affect 
                certain features of our platform.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">9. International Data Transfers</h2>
              <p className="text-text-secondary leading-relaxed">
                Your data may be processed in countries outside the UAE. When we transfer data internationally, 
                we ensure appropriate safeguards are in place, including:
              </p>
              <ul className="list-disc list-inside text-text-secondary mt-4 space-y-2">
                <li>Adequacy decisions by UAE authorities</li>
                <li>Standard contractual clauses</li>
                <li>Certification schemes recognized by the UAE</li>
              </ul>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">10. Children's Privacy</h2>
              <p className="text-text-secondary leading-relaxed">
                Our Services are not intended for individuals under 21 years of age. We do not knowingly 
                collect personal information from children. If we become aware that we have collected 
                data from a child, we will take steps to delete it promptly.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">11. Changes to This Policy</h2>
              <p className="text-text-secondary leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material 
                changes by posting the new Privacy Policy on this page and updating the "Last updated" date. 
                We encourage you to review this Privacy Policy periodically.
              </p>
            </section>

            <section className="glass-card p-6 sm:p-8 rounded-xl">
              <h2 className="text-xl sm:text-2xl font-playfair font-semibold mb-4 text-gold">12. Contact Us</h2>
              <p className="text-text-secondary leading-relaxed">
                If you have questions about this Privacy Policy or our data practices, please contact our 
                Data Protection Officer:
              </p>
              <div className="mt-4 text-text-secondary">
                <p><strong>ASTIIV BASGAT LLC</strong></p>
                <p>Data Protection Officer</p>
                <p>Dubai, United Arab Emirates</p>
                <p>Email: privacy@dreamlife.ae</p>
                <p>Phone: +971 4 XXX XXXX</p>
              </div>
              <p className="text-text-secondary leading-relaxed mt-4">
                You also have the right to lodge a complaint with the UAE Data Office or relevant 
                supervisory authority if you believe your rights have been violated.
              </p>
            </section>
          </div>

          {/* Footer */}
          <div className="mt-12 text-center">
            <Button 
              onClick={() => navigate('/')}
              className="bg-gold text-black hover:bg-gold-light"
            >
              Back to Home
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default PrivacyPage;
